import jwtdecode from 'jwt-decode';
import React from 'react';
import { PERSONA_TYPES, retrieveAuthTokens, Persona } from '@cowbell/data';

export interface TokenPayload {
  readonly exp: number;
  readonly at: Persona;
  readonly st: Persona;
  readonly tt: string;
  readonly rlm: 'ACCOUNT' | 'TEAM';
  readonly cpx?: string;
  readonly acct?: string;
  /** The signed-in account's name (JwtUtility.ACCOUNT_NAME_CLAIM). */
  readonly an?: string;
  /** The signed-in account's tenant (JwtUtility.TENANT_ID_CLAIM). */
  readonly tid?: string;
}

export const PERSONA_TYPE_TO_BASE_PATH_MAPPING: Record<Persona, string> = {
  COWBELL: 'admin',
  DIGITAL: 'da',
  MSSP: 'mssp',
  CARRIER: 'carrier',
  VENDOR: 'vendor',
  CLUSTER: 'cluster',
  AGENCY: 'agency',
  CUSTOMER: 'customer',
  UNKNOWN: 'unknown',
  RE_INSURER: 'reinsurer',
  PREMIUM_PROCESSOR: 'premiumprocessor',
  POOL: 'pool',
  FRANCHISE: 'admin',
};

export function useAuth(getTokenCallback = retrieveAuthTokens) {
  if (typeof getTokenCallback !== 'function') {
    throw new Error('"getTokenCallback" must be a function');
  }

  const { accessToken } = getTokenCallback();

  const tokenPayload = React.useMemo<TokenPayload | null>(() => {
    if (accessToken && typeof accessToken === 'string') {
      return jwtdecode(accessToken);
    }
    return null;
  }, [accessToken]);

  const isAuthenticated = React.useMemo(() => {
    if (!tokenPayload) return false;
    return tokenPayload.exp > Date.now() / 1000;
  }, [tokenPayload]);

  const accountType = React.useMemo(
    () => (tokenPayload !== null ? tokenPayload.at : null),
    [tokenPayload]
  );

  const subscriberType = React.useMemo(
    () => (tokenPayload !== null ? tokenPayload.st : null),
    [tokenPayload]
  );

  const accountBasePath = React.useMemo(
    () =>
      accountType !== null
        ? PERSONA_TYPE_TO_BASE_PATH_MAPPING[accountType]
        : null,
    [accountType]
  );

  const isGhosting = React.useMemo(
    () => accountType !== subscriberType,
    [accountType, subscriberType]
  );

  const isCowbellUserJumped =
    subscriberType === PERSONA_TYPES.COWBELL && accountType !== subscriberType;

  const realm = tokenPayload !== null ? tokenPayload.rlm : null;
  const carrierPrefix = tokenPayload !== null ? tokenPayload.cpx ?? null : null;
  const tokenAccountId = tokenPayload?.acct ?? null;
  const tokenAccountName = tokenPayload?.an ?? null;
  const tokenTenantId = tokenPayload?.tid ?? null;

  return {
    isAuthenticated,
    accountBasePath,
    accountType,
    subscriberType,
    isGhosting,
    isCowbellUserJumped,
    tokenType: tokenPayload !== null ? tokenPayload.tt : null,
    tokenRealm: realm,
    isAccountScoped: realm === 'ACCOUNT',
    isTeamScoped: realm === 'TEAM',
    carrierPrefix,
    tokenAccountId,
    tokenAccountName,
    tokenTenantId,
  };
}
